'use client';

import type { NotificationItem } from '@tracedocuments/types';
import { apiFetch, parseApiErrorResponse } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';

export type NotificationsListResponse = {
    items: NotificationItem[];
    unreadCount: number;
    nextCursor?: string | null;
};

const patchInit: RequestInit = {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
};

function readString(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    return String(value);
}

/** Normalizes REST/SignalR payloads (camelCase or PascalCase). */
export function normalizeNotificationItem(raw: unknown): NotificationItem {
    const item = (raw ?? {}) as Record<string, unknown>;
    return {
        id: readString(item.id ?? item.Id),
        type: readString(item.type ?? item.Type),
        title: readString(item.title ?? item.Title),
        body: readString(item.body ?? item.Body),
        data: (item.data ?? item.Data ?? null) as string | null,
        isRead: Boolean(item.isRead ?? item.IsRead ?? false),
        createdAt: readString(item.createdAt ?? item.CreatedAt),
    };
}

export async function fetchNotifications(page = 1, pageSize = 20): Promise<NotificationItem[]> {
    const result = await fetchNotificationsPage(page, pageSize);
    return result.items;
}

// Shorter than the default list timeout: a stuck request here shows a bare spinner
// with no feedback, so fail fast and let the query retry rather than compounding delays.
const NOTIFICATIONS_LIST_TIMEOUT_MS = 8_000;

/**
 * Fetches a notifications page.
 * Pass a string (or null) as the first arg to use keyset cursor mode (`cursor=`).
 * Pass a number for legacy offset `page=` mode.
 */
export async function fetchNotificationsPage(
    pageOrCursor: number | string | null = 1,
    pageSize = 20,
    unreadOnly = false,
): Promise<NotificationsListResponse> {
    const params = new URLSearchParams();
    params.set('pageSize', String(pageSize));
    if (unreadOnly) params.set('unreadOnly', 'true');
    if (typeof pageOrCursor === 'number') {
        params.set('page', String(pageOrCursor));
    } else {
        params.set('cursor', pageOrCursor ?? '');
    }

    const res = await apiFetchWithTimeout(
        `/api/notifications?${params.toString()}`,
        undefined,
        NOTIFICATIONS_LIST_TIMEOUT_MS,
    );
    if (!res.ok) {
        const apiErr = await parseApiErrorResponse(res);
        throw apiErr;
    }
    const data = (await res.json()) as NotificationsListResponse | NotificationItem[] | Record<string, unknown>;
    if (Array.isArray(data)) {
        return { items: data.map(normalizeNotificationItem), unreadCount: 0, nextCursor: null };
    }
    const record = data as Record<string, unknown>;
    const rawItems = (record.items ?? record.Items ?? []) as unknown[];
    const unreadCount = Number(record.unreadCount ?? record.UnreadCount ?? 0);
    const nextCursorRaw = record.nextCursor ?? record.NextCursor;
    const nextCursor =
        typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
    return {
        items: Array.isArray(rawItems) ? rawItems.map(normalizeNotificationItem) : [],
        unreadCount: Number.isFinite(unreadCount) ? unreadCount : 0,
        nextCursor,
    };
}

export async function fetchNotification(id: string): Promise<NotificationItem | null> {
    if (!id) return null;
    const res = await apiFetch(`/api/notifications/${encodeURIComponent(id)}`);
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return normalizeNotificationItem(await res.json());
}

export async function fetchUnreadCount(): Promise<number> {
    try {
        const res = await apiFetch('/api/notifications/unread-count');
        if (!res.ok) return 0;
        const data = (await res.json()) as { unreadCount?: number; UnreadCount?: number; count?: number };
        return data.unreadCount ?? data.UnreadCount ?? data.count ?? 0;
    } catch {
        return 0;
    }
}

export async function markAsRead(id: string): Promise<boolean> {
    if (!id) return false;
    const res = await apiFetch(`/api/notifications/${encodeURIComponent(id)}/read`, patchInit);
    return res.ok;
}

export async function markAsOpened(id: string): Promise<boolean> {
    if (!id) return false;
    const res = await apiFetch(`/api/notifications/${encodeURIComponent(id)}/opened`, patchInit);
    return res.ok;
}

export async function markAllAsRead(): Promise<boolean> {
    const res = await apiFetch('/api/notifications/read-all', patchInit);
    return res.ok;
}

export async function deleteNotification(id: string): Promise<boolean> {
    if (!id) return false;
    const res = await apiFetch(`/api/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return res.ok;
}

export async function deleteAllNotifications(): Promise<boolean> {
    const res = await apiFetch('/api/notifications/all', { method: 'DELETE' });
    return res.ok;
}

export async function getNotificationActionError(response: Response): Promise<string> {
    const apiErr = await parseApiErrorResponse(response);
    return apiErr.message;
}

export type { NotificationItem } from '@tracedocuments/types';
